import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Database, HelpCircle, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PricingSection from '../components/PricingSection';
import AuthModal from '../components/AuthModal';
import { useAuth } from '../context/AuthContext';

const faqs = [
  {
    question: 'What happens to my data?',
    answer: 'Your data stays private by default. With the free plan, all processing happens in your browser - data never leaves your machine. Paid plans offer optional cloud sync for backup and collaboration, but you control when and what to sync.'
  },
  {
    question: 'Can I cancel anytime?',
    answer: 'Yes, you can cancel your subscription at any time from your billing portal. You\'ll continue to have access to paid features until the end of your billing period.'
  },
  {
    question: 'What file formats are supported?',
    answer: 'DuckPilot supports CSV, Excel (.xlsx), JSON, and Parquet files. We handle most common data formats used in business analytics.'
  },
  {
    question: 'How does the AI query translation work?',
    answer: 'Type questions in plain English like "What were last month\'s top products?" and our AI translates it to SQL, runs the query on your data, and returns results with charts. No SQL knowledge required.'
  },
  {
    question: 'Is there a file size limit?',
    answer: 'Free plans support files up to 50MB. Pro and Team plans support files up to 500MB. For larger datasets, contact us about Enterprise solutions.'
  },
  {
    question: 'Do you offer refunds?',
    answer: 'We offer a 14-day money-back guarantee. If you\'re not satisfied within the first 14 days, contact us for a full refund.'
  }
];

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-[rgba(32,30,29,0.12)]">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between py-4 text-left"
      >
        <span className="font-medium text-ink">{question}</span>
        <ChevronDown
          className={`h-5 w-5 text-sand-600 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="pb-4 text-sm text-sand-700"
        >
          {answer}
        </motion.div>
      )}
    </div>
  );
};

const PricingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  return (
    <div className="min-h-screen bg-paper text-ink">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-[rgba(32,30,29,0.12)] bg-paper">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center gap-3 text-sand-700 hover:text-ink"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-[rgba(32,30,29,0.12)] bg-white">
              <Database className="h-4 w-4" />
            </div>
            <span className="text-lg font-semibold tracking-tight">DuckPilot</span>
          </button>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="rounded-full bg-bill-500 px-4 py-2 text-sm font-semibold text-white hover:bg-bill-600 shadow-brand-sm"
              >
                Go to Dashboard
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowAuthModal(true)}
                className="rounded-full border border-[rgba(32,30,29,0.18)] bg-white px-4 py-2 text-sm font-semibold text-ink hover:bg-sand-100"
              >
                Sign in
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="mx-auto max-w-6xl px-6 pt-16 pb-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Choose your plan
          </h1>
          <p className="text-lg text-sand-700 max-w-xl mx-auto">
            Start free with local-first analytics. Upgrade for unlimited queries,
            cloud sync, and team collaboration.
          </p>
        </motion.div>
      </div>

      {/* Pricing Section */}
      <div className="mx-auto max-w-6xl px-6 py-8">
        <PricingSection
          showHeader={false}
          variant="full"
          onAuthRequired={() => setShowAuthModal(true)}
        />
      </div>

      {/* Comparison Table */}
      <div className="mx-auto max-w-4xl px-6 py-16">
        <h2 className="text-2xl font-bold text-center mb-8">Compare plans</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[rgba(32,30,29,0.12)]">
                <th className="py-3 px-4 text-left text-sand-600 font-medium">Feature</th>
                <th className="py-3 px-4 text-center text-sand-600 font-medium">Free</th>
                <th className="py-3 px-4 text-center text-sand-600 font-medium">Pro</th>
                <th className="py-3 px-4 text-center text-sand-600 font-medium">Team</th>
                <th className="py-3 px-4 text-center text-sand-600 font-medium">Enterprise</th>
              </tr>
            </thead>
            <tbody className="text-sand-700">
              <tr className="border-b border-[rgba(32,30,29,0.12)]">
                <td className="py-3 px-4">Monthly uploads</td>
                <td className="py-3 px-4 text-center">5</td>
                <td className="py-3 px-4 text-center">Unlimited</td>
                <td className="py-3 px-4 text-center">Unlimited</td>
                <td className="py-3 px-4 text-center">Unlimited</td>
              </tr>
              <tr className="border-b border-[rgba(32,30,29,0.12)]">
                <td className="py-3 px-4">AI queries</td>
                <td className="py-3 px-4 text-center">50/month</td>
                <td className="py-3 px-4 text-center">Unlimited</td>
                <td className="py-3 px-4 text-center">Unlimited</td>
                <td className="py-3 px-4 text-center">Unlimited</td>
              </tr>
              <tr className="border-b border-[rgba(32,30,29,0.12)]">
                <td className="py-3 px-4">Storage</td>
                <td className="py-3 px-4 text-center">500 MB</td>
                <td className="py-3 px-4 text-center">10 GB</td>
                <td className="py-3 px-4 text-center">50 GB</td>
                <td className="py-3 px-4 text-center">Custom</td>
              </tr>
              <tr className="border-b border-[rgba(32,30,29,0.12)]">
                <td className="py-3 px-4">Team members</td>
                <td className="py-3 px-4 text-center">1</td>
                <td className="py-3 px-4 text-center">1</td>
                <td className="py-3 px-4 text-center">5</td>
                <td className="py-3 px-4 text-center">Unlimited</td>
              </tr>
              <tr className="border-b border-[rgba(32,30,29,0.12)]">
                <td className="py-3 px-4">Cloud sync</td>
                <td className="py-3 px-4 text-center text-sand-600">-</td>
                <td className="py-3 px-4 text-center text-sage-600">Yes</td>
                <td className="py-3 px-4 text-center text-sage-600">Yes</td>
                <td className="py-3 px-4 text-center text-sage-600">Yes</td>
              </tr>
              <tr className="border-b border-[rgba(32,30,29,0.12)]">
                <td className="py-3 px-4">PDF export</td>
                <td className="py-3 px-4 text-center text-sand-600">-</td>
                <td className="py-3 px-4 text-center text-sage-600">Yes</td>
                <td className="py-3 px-4 text-center text-sage-600">Yes</td>
                <td className="py-3 px-4 text-center text-sage-600">Yes</td>
              </tr>
              <tr className="border-b border-[rgba(32,30,29,0.12)]">
                <td className="py-3 px-4">Shared dashboards</td>
                <td className="py-3 px-4 text-center text-sand-600">-</td>
                <td className="py-3 px-4 text-center text-sand-600">-</td>
                <td className="py-3 px-4 text-center text-sage-600">Yes</td>
                <td className="py-3 px-4 text-center text-sage-600">Yes</td>
              </tr>
              <tr className="border-b border-[rgba(32,30,29,0.12)]">
                <td className="py-3 px-4">API access</td>
                <td className="py-3 px-4 text-center text-sand-600">-</td>
                <td className="py-3 px-4 text-center text-sand-600">-</td>
                <td className="py-3 px-4 text-center text-sage-600">Yes</td>
                <td className="py-3 px-4 text-center text-sage-600">Yes</td>
              </tr>
              <tr className="border-b border-[rgba(32,30,29,0.12)]">
                <td className="py-3 px-4">SSO/SAML</td>
                <td className="py-3 px-4 text-center text-sand-600">-</td>
                <td className="py-3 px-4 text-center text-sand-600">-</td>
                <td className="py-3 px-4 text-center text-sand-600">-</td>
                <td className="py-3 px-4 text-center text-sage-600">Yes</td>
              </tr>
              <tr className="border-b border-[rgba(32,30,29,0.12)]">
                <td className="py-3 px-4">Support</td>
                <td className="py-3 px-4 text-center">Community</td>
                <td className="py-3 px-4 text-center">Email</td>
                <td className="py-3 px-4 text-center">Priority</td>
                <td className="py-3 px-4 text-center">Dedicated</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="flex items-center gap-3 mb-8">
          <HelpCircle className="h-6 w-6 text-sand-600" />
          <h2 className="text-2xl font-bold">Frequently asked questions</h2>
        </div>
        <div className="rounded-2xl border border-[rgba(32,30,29,0.12)] bg-white shadow-brand-sm p-6">
          {faqs.map((faq, index) => (
            <FAQItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <div className="rounded-3xl border border-[rgba(32,30,29,0.12)] bg-gradient-to-br from-bill-100 to-sage-100 shadow-brand-sm p-8 md:p-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to get started?
          </h2>
          <p className="text-sand-700 mb-6 max-w-md mx-auto">
            Join thousands of analysts and consultants who trust DuckPilot for
            their data analysis needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => {
                if (isAuthenticated) {
                  navigate('/dashboard');
                } else {
                  setShowAuthModal(true);
                }
              }}
              className="rounded-full bg-bill-500 px-6 py-3 text-sm font-semibold text-white hover:bg-bill-600 shadow-brand-sm"
            >
              Start for free
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard?mode=demo&sample=ecommerce')}
              className="rounded-full border border-[rgba(32,30,29,0.18)] bg-white px-6 py-3 text-sm font-semibold text-ink hover:bg-sand-100"
            >
              Try the demo
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[rgba(32,30,29,0.12)] bg-paper">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-10 text-sm text-sand-600 md:flex-row md:items-center md:justify-between">
          <span>DuckPilot. Local-first analytics.</span>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="hover:text-ink"
            >
              Home
            </button>
            <button
              type="button"
              onClick={() => navigate('/pricing')}
              className="hover:text-ink"
            >
              Pricing
            </button>
            <button
              type="button"
              onClick={() => {
                if (isAuthenticated) {
                  navigate('/dashboard');
                } else {
                  setShowAuthModal(true);
                }
              }}
              className="hover:text-ink"
            >
              Dashboard
            </button>
          </div>
        </div>
      </footer>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={() => navigate('/dashboard')}
      />
    </div>
  );
};

export default PricingPage;
